import React, {useMemo, useState} from 'react';
import {ApolloClient, NormalizedCacheObject} from '@apollo/client';
import {useParams} from 'react-router-dom';
import {
  Avatar,
  Box,
  Chip,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Typography,
} from '@mui/material';
import {useGetAuthor} from '../../api/graphql_author';
import {VideoCard} from '../YouTube/VideoCard';
import PendingActionBanner from './PendingActionBanner';
import {pcolors, cardSx, sectionLabelSx} from '../../theme/planapusTheme';
import {VideoRating, ScoreDirection} from '../../types/generated/graphql';
import {getScoreColor} from '../../utils/scoring';

interface Props {
  client: ApolloClient<NormalizedCacheObject>;
}

type Filter = 'all' | 'rated' | 'unrated' | 'beneficial' | 'neutral' | 'harmful';

export default function OneAuthor({client}: Props) {
  const {pid, aid} = useParams();
  const {data, loading, error} = useGetAuthor(client, pid as string, aid as string);
  const [filter, setFilter] = useState<Filter>('all');

  const agg = data?.getAggregateScoreForAuthor;
  // `videos` is the full per-author library — rated AND unrated.
  // `score/rating` are nullable on AuthorVideoScore; null means the
  // video exists in the author's catalogue but has no score row.
  const allVideos = useMemo(() => agg?.videos ?? [], [agg]);

  const filteredVideos = useMemo(() => {
    switch (filter) {
      case 'rated':
        return allVideos.filter(v => v.rating != null);
      case 'unrated':
        return allVideos.filter(v => v.rating == null);
      case 'beneficial':
        return allVideos.filter(v => v.rating === VideoRating.Beneficial);
      case 'neutral':
        return allVideos.filter(v => v.rating === VideoRating.Neutral);
      case 'harmful':
        return allVideos.filter(v => v.rating === VideoRating.Harmful);
      case 'all':
      default:
        return allVideos;
    }
  }, [allVideos, filter]);

  const counts = useMemo(() => {
    let rated = 0,
      beneficial = 0,
      neutral = 0,
      harmful = 0;
    for (const v of allVideos) {
      if (v.rating != null) {
        rated++;
        if (v.rating === VideoRating.Beneficial) beneficial++;
        if (v.rating === VideoRating.Neutral) neutral++;
        if (v.rating === VideoRating.Harmful) harmful++;
      }
    }
    return {
      total: allVideos.length,
      rated,
      unrated: allVideos.length - rated,
      beneficial,
      neutral,
      harmful,
    };
  }, [allVideos]);

  if (loading) return <Typography>Loading channel…</Typography>;
  if (error) return <Typography>Error: {error.message}</Typography>;
  if (!agg) return <Typography>Channel not found</Typography>;

  // Pick an author avatar from one of the videos — the aggregate
  // doesn't return the author object directly, but every videos[].video
  // has the same author.
  const authorRef = allVideos[0]?.video.author;

  return (
    <Box>
      {pid && aid && (
        <PendingActionBanner client={client} profileId={pid} channelId={aid} />
      )}
      {/* Hero header — big score, channel identity, and the meta row.
          The score is the headline here; everything else supports it. */}
      {(() => {
        // Channel aggregates are higher-is-better; direction is implicit.
        const scoreColor = getScoreColor(agg.score, ScoreDirection.HigherIsBetter);
        return (
          <Box
            sx={{
              ...cardSx,
              p: 2.5,
              mb: 3,
              display: 'grid',
              gap: 2,
              gridTemplateColumns: {xs: '1fr', sm: 'auto 1fr'},
              alignItems: 'center',
            }}
          >
            {/* Score medallion — sized so it dominates the header
                immediately. Restricted channels render a red disc with
                the rating in place of a number. */}
            <Box
              sx={{
                width: 128,
                height: 128,
                borderRadius: '50%',
                bgcolor: `${scoreColor}22`,
                color: scoreColor,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                flex: 'none',
                fontVariantNumeric: 'tabular-nums',
                mx: {xs: 'auto', sm: 0},
              }}
            >
              <Typography sx={{fontSize: 42, fontWeight: 900, lineHeight: 1, color: 'inherit'}}>
                {agg.score.toFixed(1)}
              </Typography>
              <Typography
                sx={{
                  fontSize: 10,
                  fontWeight: 700,
                  letterSpacing: 0.6,
                  textTransform: 'uppercase',
                  color: 'inherit',
                  mt: 0.5,
                  opacity: 0.8,
                }}
              >
                / 10
              </Typography>
            </Box>

            <Box sx={{minWidth: 0}}>
              <Box sx={{display: 'flex', alignItems: 'center', gap: 1.5}}>
                {authorRef?.thumbnails?.[0]?.url && (
                  <Avatar
                    src={authorRef.thumbnails[0].url}
                    sx={{width: 40, height: 40, flex: 'none'}}
                  />
                )}
                <Box sx={{minWidth: 0}}>
                  <Typography
                    sx={{fontSize: 20, fontWeight: 700, color: pcolors.textPrimary, lineHeight: 1.25}}
                    noWrap
                  >
                    {authorRef?.name || `Channel ${aid}`}
                  </Typography>
                  <Typography sx={{fontSize: 12, color: pcolors.textSecondary, mt: 0.25}}>
                    {String(agg.rating).toLowerCase()} · confidence{' '}
                    {String(agg.confidence).toLowerCase()} · {counts.total} video
                    {counts.total === 1 ? '' : 's'}
                    {agg.restricted && ' · restricted'}
                  </Typography>
                </Box>
              </Box>
              {agg.explanation && (
                <Typography
                  sx={{fontSize: 13, color: pcolors.textSecondary, mt: 1.25, lineHeight: 1.55}}
                >
                  {agg.explanation}
                </Typography>
              )}
            </Box>
          </Box>
        );
      })()}

      {/* Filter bar + per-state counts. */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          mb: 2,
          flexWrap: 'wrap',
        }}
      >
        <FormControl size="small" sx={{minWidth: 180}}>
          <InputLabel>Filter</InputLabel>
          <Select
            value={filter}
            label="Filter"
            onChange={(e: SelectChangeEvent) => setFilter(e.target.value as Filter)}
          >
            <MenuItem value="all" sx={{fontSize: 13}}>
              All videos ({counts.total})
            </MenuItem>
            <MenuItem value="rated" sx={{fontSize: 13}}>
              Rated ({counts.rated})
            </MenuItem>
            <MenuItem value="unrated" sx={{fontSize: 13}}>
              Unrated ({counts.unrated})
            </MenuItem>
            <MenuItem value="beneficial" sx={{fontSize: 13}}>
              Beneficial ({counts.beneficial})
            </MenuItem>
            <MenuItem value="neutral" sx={{fontSize: 13}}>
              Neutral ({counts.neutral})
            </MenuItem>
            <MenuItem value="harmful" sx={{fontSize: 13}}>
              Harmful ({counts.harmful})
            </MenuItem>
          </Select>
        </FormControl>
        <Chip
          size="small"
          label={`${filteredVideos.length} shown`}
          sx={{fontSize: 11, bgcolor: pcolors.borderLight, color: pcolors.textSecondary}}
        />
      </Box>

      {/* Video grid — same layout conventions as the admin videos page. */}
      {filteredVideos.length === 0 ? (
        <Typography sx={{fontSize: 13, color: pcolors.textMuted}}>
          No videos match the current filter.
        </Typography>
      ) : (
        <Grid container spacing={2}>
          {filteredVideos.map(v => (
            <Grid key={v.id} size={{xs: 6, sm: 4, md: 3, lg: 2}}>
              <Box sx={{position: 'relative'}}>
                <VideoCard pid={pid as string} client={client} video={v.video} />
                {/* Small corner chip telling rated-vs-unrated at a
                    glance without opening the video page. */}
                <Box
                  sx={{
                    position: 'absolute',
                    top: 6,
                    right: 6,
                    px: 0.75,
                    py: 0.25,
                    borderRadius: 1,
                    fontSize: 10,
                    fontWeight: 700,
                    letterSpacing: 0.3,
                    textTransform: 'uppercase',
                    boxShadow: '0 1px 2px rgba(0,0,0,0.12)',
                    bgcolor:
                      v.rating == null
                        ? pcolors.borderLight
                        : v.rating === VideoRating.Beneficial
                          ? pcolors.green[50]
                          : v.rating === VideoRating.Harmful
                            ? pcolors.red[50]
                            : pcolors.amber[50],
                    color:
                      v.rating == null
                        ? pcolors.textSecondary
                        : v.rating === VideoRating.Beneficial
                          ? pcolors.green[700]
                          : v.rating === VideoRating.Harmful
                            ? pcolors.red[700]
                            : pcolors.amber[700],
                    pointerEvents: 'none',
                  }}
                >
                  {v.rating == null
                    ? 'unrated'
                    : v.score != null
                      ? `${v.score.toFixed(1)}`
                      : String(v.rating).toLowerCase()}
                </Box>
              </Box>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Keep the Watched / Recommended sections below — they give a
          per-profile lens on top of the channel-wide list. Most users
          can ignore them; admins auditing channel drift care. */}
      {agg.watched.length > 0 && (
        <Box sx={{mt: 4}}>
          <Typography sx={sectionLabelSx}>
            Watched by this profile ({agg.watched.length})
          </Typography>
          <Grid container spacing={2} sx={{mt: 1}}>
            {agg.watched.map(video => (
              <Grid key={video.id} size={{xs: 6, sm: 4, md: 3, lg: 2}}>
                <VideoCard pid={pid as string} client={client} video={video} />
              </Grid>
            ))}
          </Grid>
        </Box>
      )}

      {agg.recommended.length > 0 && (
        <Box sx={{mt: 4}}>
          <Typography sx={sectionLabelSx}>
            Recommended to this profile ({agg.recommended.length})
          </Typography>
          <Grid container spacing={2} sx={{mt: 1}}>
            {agg.recommended.map(video => (
              <Grid key={video.id} size={{xs: 6, sm: 4, md: 3, lg: 2}}>
                <VideoCard pid={pid as string} client={client} video={video} />
              </Grid>
            ))}
          </Grid>
        </Box>
      )}
    </Box>
  );
}
